// §11.3 — Notepad's text logic, kept pure so find/replace can be tested exhaustively.
//
// Search is literal, never a regular expression: the string a user types into a find box is
// the string they want to find, and `.` in a filename should not match every character.

export interface Match {
  start: number;
  end: number;
}

/** Every non-overlapping occurrence of `query`, left to right. */
export function findMatches(text: string, query: string, matchCase: boolean): Match[] {
  if (query.length === 0) return [];
  const haystack = matchCase ? text : text.toLowerCase();
  const needle = matchCase ? query : query.toLowerCase();
  const matches: Match[] = [];
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at < 0) return matches;
    matches.push({ start: at, end: at + query.length });
    from = at + query.length;
  }
}

/**
 * The match to jump to from `caret`, wrapping at either end. Returns `-1` when there are none.
 * Wrapping is what makes Find-next usable; stopping at the last match makes it feel broken.
 */
export function nextMatchIndex(matches: readonly Match[], caret: number, forward: boolean): number {
  if (matches.length === 0) return -1;
  if (forward) {
    const found = matches.findIndex((match) => match.start >= caret);
    return found === -1 ? 0 : found;
  }
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index];
    if (match && match.end <= caret) return index;
  }
  return matches.length - 1;
}

/** Replace one span. The caller supplies the span, so this cannot drift from what was shown. */
export function replaceAt(text: string, match: Match, replacement: string): string {
  return text.slice(0, match.start) + replacement + text.slice(match.end);
}

/**
 * Replace every occurrence in one pass, right to left, so earlier offsets stay valid — and so
 * replacing `a` with `aa` cannot loop forever the way a naive `while (indexOf)` does.
 */
export function replaceAll(
  text: string,
  query: string,
  replacement: string,
  matchCase: boolean,
): { text: string; count: number } {
  const matches = findMatches(text, query, matchCase);
  let out = text;
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index];
    if (match) out = replaceAt(out, match, replacement);
  }
  return { text: out, count: matches.length };
}

/**
 * How many line numbers the gutter needs. A trailing newline opens a line the caret can sit on,
 * so `"a\n"` is two lines — which is what every editor's gutter shows.
 */
export function lineCount(text: string): number {
  let lines = 1;
  for (const char of text) if (char === '\n') lines += 1;
  return lines;
}

/** The line and column of a caret offset, both 1-based, for the status bar. */
export function caretPosition(text: string, caret: number): { line: number; column: number } {
  const before = text.slice(0, Math.max(0, Math.min(caret, text.length)));
  const lastBreak = before.lastIndexOf('\n');
  return { line: lineCount(before), column: before.length - lastBreak };
}

/** `Untitled`, then `Untitled 2` — a second new tab must not look like the first. */
export function untitledName(taken: readonly string[], base: string): string {
  if (!taken.includes(base)) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base} ${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
}
